<template>
  <div
    class="card mb-3"
    @click="$emit('projectSelected', project.id)"
    style="cursor: pointer"
  >
    <div class="card-body d-flex">
      <!-- Left Side - Project Name -->
      <div class="left-section" @click="$emit('projectSelected', project.id)">
        <h4 class="card-title">{{ project.project_name }}</h4>
      </div>

      <!-- Center Section - Project Details -->
      <div class="center-section" @click="$emit('projectSelected', project.id)">
        <p class="card-text mb-1">
          <strong>{{ $t("project.address") }}:</strong>
          {{ fullAddress }} <br />
          <strong>{{ $t("project.owner") }}:</strong>
          {{ project.client_email }}
          <br />
          <strong>{{ $t("project.created") }}:</strong>
          {{ formatDate(project.created_at) }} <br />
          <strong>{{ $t("project.lastModified") }}:</strong>
          {{ formatDate(project.updated_at) }} <br />
          <strong>{{ $t("project.capacity") }}:</strong>
          {{ project.total_power }} kW
        </p>
      </div>

      <!-- Right Section - Buttons -->
      <div class="right-section">
        <button
          @click.stop="handleDuplicate"
          class="btn btn-warning btn-sm me-2"
        >
          {{ $t("project.actions.duplicate") }}
        </button>
        <button
          @click.stop="showDeleteModal = true"
          class="btn btn-danger btn-sm"
        >
          {{ $t("project.actions.delete") }}
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal - Rendered outside the card -->
  <Teleport to="body">
    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click="showDeleteModal = false"
    >
      <div class="modal-content" @click.stop>
        <h3 class="text-center mb-3">{{ $t("project.deleteModal.title") }}</h3>
        <p class="text-center mb-4">
          {{ $t("project.deleteModal.message") }}
        </p>
        <div class="d-flex justify-content-center gap-3">
          <button
            @click="showDeleteModal = false"
            class="btn btn-secondary px-4"
          >
            {{ $t("project.deleteModal.cancel") }}
          </button>
          <button @click="handleDelete" class="btn btn-danger px-4">
            {{ $t("project.deleteModal.delete") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from "vue";
import { ProjectSummary } from "@/types/projectTypes";
import { useProjectStore } from "@/stores/project";
import { useMapStateStore } from "@/stores/mapState";
import { useProjectSummaryStore } from "@/stores/project";
import { useRouter } from "vue-router";

export default defineComponent({
  name: "ProjectSummaryComponent",
  props: {
    project: {
      type: Object as () => ProjectSummary,
      required: true,
    },
  },
  setup(props, { emit }) {
    const projectStore = useProjectStore();
    const mapStateStore = useMapStateStore();
    const projectSummaryStore = useProjectSummaryStore();
    const router = useRouter();
    const showDeleteModal = ref(false);

    const fullAddress = computed(() => {
      return `${props.project.address}, ${props.project.city}, ${props.project.country}`;
    });

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const handleDelete = async () => {
      try {
        await projectStore.deleteProject(props.project.id);
        await projectSummaryStore.fetchProjectSummaries();
        showDeleteModal.value = false;
      } catch (error) {
        console.error("Failed to delete project:", error);
        // TODO: Show error message to user
      }
    };

    const handleDuplicate = async () => {
      try {
        // Clear current project and map state
        await projectStore.clearProject();
        await mapStateStore.clearMapState();

        // Fetch the project to duplicate
        const projectToDuplicate = await projectStore.fetchProject(
          props.project.id
        );

        if (projectToDuplicate) {
          // Create a copy of the project with modified fields
          const duplicatedProject = {
            ...projectToDuplicate,
            id: 0, // Set ID to 0 for new project
            project_name: `${projectToDuplicate.project_name}_copy`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            status: "draft" as const,
          };

          // Set the duplicated project in the store
          projectStore.$patch({ project: duplicatedProject });

          // Navigate to the project view
          router.push("/project");
        }
      } catch (error) {
        console.error("Failed to duplicate project:", error);
        // TODO: Show error message to user
      }
    };

    return {
      fullAddress,
      formatDate,
      showDeleteModal,
      handleDelete,
      handleDuplicate,
    };
  },
});
</script>

<style scoped>
.card {
  background: var(--color-white);
  border: none;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s;
  position: relative;
}

.card::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 10px;
  height: 100%;
  background-color: var(--tertiary-color);
  border-top-left-radius: 2px;
  border-bottom-left-radius: 2px;
}

.card-body {
  color: var(--color-dark-dark);
  position: relative;
  z-index: 1;
  padding: 1.5rem;
}

.left-section {
  width: 25%;
  padding-right: 1.5rem;
  border-right: 1px solid var(--color-light-gray);
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.center-section {
  width: 50%;
  padding: 0 1.5rem;
}

.right-section {
  width: 25%;
  padding-left: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 0.5rem;
}

.card-title {
  color: var(--color-dark-dark);
  font-weight: 400;
  font-size: 1.5rem;
  margin: 0;
  text-align: left;
}

.card-text {
  color: var(--color-dark-dark);
  margin: 0;
}

.card-text strong {
  color: var(--color-dark-dark);
}

.project-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  border: 2px solid rgba(175, 154, 17, 0.73);
  cursor: pointer;
}

.project-icon i {
  font-size: 32px;
  color: var(--color-white);
}

.flex-grow-1 {
  cursor: pointer;
}

/* Modal Styles */
.modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  pointer-events: auto;
}

.modal-content {
  background-color: var(--color-white);
  padding: 24px;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  color: var(--color-dark);
}

.modal-content h3 {
  margin-top: 0;
  color: var(--color-red);
  font-weight: 600;
}

.modal-content p {
  color: var(--color-dark);
  line-height: 1.5;
}

.modal-content .btn {
  min-width: 100px;
  font-weight: 500;
  border-radius: 2px;
}

.modal-content .btn-secondary {
  background-color: var(--color-gray);
  border-color: var(--color-gray);
  color: var(--color-white);
}

.modal-content .btn-secondary:hover {
  background-color: var(--color-dark-dark);
  border-color: var(--color-dark-dark);
}

.modal-content .btn-danger {
  background-color: var(--color-red);
  border-color: var(--color-red);
}

.btn-warning {
  background-color: transparent;
  border: 2px solid var(--primary-color);
  color: var(--primary-color);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.btn-warning:hover {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--color-white);
}

.btn-danger {
  background-color: var(--color-red);
  border-color: var(--color-red);
  color: var(--color-white);
  border-radius: 2px;
}

.btn-danger:hover {
  background-color: var(--color-red);
  border-color: var(--color-red);
}
</style>
